import { faCheckCircle } from "@fortawesome/pro-regular-svg-icons/faCheckCircle";
import { faCheckSquare } from "@fortawesome/pro-regular-svg-icons/faCheckSquare";
import { faSquare } from "@fortawesome/pro-regular-svg-icons/faSquare";
import { faTimesCircle } from "@fortawesome/pro-regular-svg-icons/faTimesCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Checkbox, Grid, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { alpha, Theme } from '@mui/material/styles';
import { ClassKeyOfStyles, createStyles, StyledComponentProps, withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { AppImages } from "assets/appResources";
import { GlobalClassKeys, TimelineMinWidth } from "assets/jss/common-styles";
import classNames from 'classnames';
import { AwardSnackbarMessage } from "components/AwardSnackbarMessage";
import { RequestIndicator } from "components/CallServer";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import IconCorrectAnswers from "components/Icons/IconCorrectAnswers";
import IconEvolution from "components/Icons/IconEvolution";
import IconQuiz from "components/Icons/IconQuiz";
import IconTimeline from "components/Icons/IconTimeline";
import IconTrophyAlt from "components/Icons/IconTrophyAlt";
import ImageZoom from "components/ImageZoom";
import MuiRegularButton from "components/MuiExtras/Button/MuiRegularButton";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiBox from "components/MuiExtras/MuiBox";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPaper from "components/MuiExtras/MuiPaper";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import ProgressBar from "components/ProgressBar";
import { ReactSplitString } from "components/ReactSplitString";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import KnowledgeTestTrustLevelName from "components/Translations/KnowledgeTestTrustLevelName";
import { KnowledgeTestSelectionMode, KnowledgeTestTrustLevel } from "models/ApiEnums";
import { AwardResult, QuizAlternativeDto, QuizAnswerResultDto, QuizItemDto, QuizSessionDto, QuizSessionResultModel } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router";
import { RoutePaths } from "routes/RouteConfig";
import Session from "services/Session";
import KnowledgeTopicLevel from "views/KnowledgeTest/Components/KnowledgeTopicLevel";
import QuizVideo from "views/Quiz/Components/QuizVideo";


/////////////////////////////////////////////////////////////////////////////////////////////////

const styles = (theme: Theme) => createStyles({

    simpleChoiseContainer: {
        width: "100%",
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        borderRadius: "20px",
        borderStyle: "solid",
        borderWidth: 1,
        backgroundColor: theme.palette.common.white,
    },

    simpleChoiseItem: {
        cursor: "pointer",
        borderColor: theme.genie.palette.info.main,
        "&:hover": {
            backgroundColor: alpha(theme.genie.palette.info.main, 0.5),
        },
    },

    simpleChoiseResultItem: {
        borderColor: theme.palette.grey[500],
    },

    simpleChoiceSelected: {
        backgroundColor: alpha(theme.genie.palette.info.main, 0.7),
    },

    simpleChoiceCorrect: {
        backgroundColor: alpha(theme.genie.palette.success.main, 0.7),
    },

    simpleChoiceIncorrect: {
        backgroundColor: alpha(theme.palette.error.main, 0.7),
        borderColor: theme.palette.grey[500],
    },

    multiChoiceCorrect: {
        color: theme.genie.palette.success.main,
    },

    multiChoiceIncorrect: {
        color: theme.palette.error.main,
    },

    itemFeedback: {
        width: "1px",
        color: theme.palette.grey[500],
    },
    itemFeedbackCorrect: {
        color: theme.genie.palette.success.main
    },
    itemFeedbackIncorrect: {
        color: theme.palette.error.main
    },

    itemSelected: {
        fontWeight: "bold"
    },
    resultTable: {
        width: "100%"
    },

    container: {
        width: TimelineMinWidth,
        marginRight: "auto",
        marginLeft: "auto",
    },

    tooltip: {
        position: "relative",
        width: "100%",
        minHeight: "60px",
        padding: "20px",
        borderRadius: "10px",
        marginLeft: "10px",
        "& ul": {
            listStyleType: "square",
            padding: "0 8px",
            margin: 0
        },
        "&::after": {
            content: '" "',
            position: "absolute",
            left: "-10px",
            top: "20px",
            width: "20px",
            height: "20px",
            background: "inherit",
            transform: "rotate(135deg)",
        },
        "&.correct": {
            boxShadow: `inset 0px 0px 5px ${theme.genie.palette.success.dark}`,
            backgroundColor: "#ccffcc",
            "&::after": {
                boxShadow: `inset -5px -5px 5px -4px ${theme.genie.palette.success.dark}`
            }
        },
        "&.incorrect": {
            boxShadow: `inset 0px 0px 5px ${theme.palette.error.dark}`,
            backgroundColor: "#ffcccc",
            "&::after": {
                boxShadow: `inset -5px -5px 5px -4px ${theme.palette.error.dark}`
            }
        }
    }
});

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IQuizChoiceProps {
    item: QuizItemDto;
    selectedTests: string[];
    onSelect: (item: QuizAlternativeDto) => void;
}

const QuizMultiChoice: React.FC<IQuizChoiceProps> = (props) => {
    const { item, selectedTests, onSelect } = props;
    return (
        <div>
            {
                item.alternatives.map((v, index) => {
                    return (
                        <ListItem key={index} dense button onClick={() => onSelect(v)}>
                            <ListItemIcon>
                                <Checkbox
                                    edge="start"
                                    checked={selectedTests.findIndex(p => p == v.alternativeId) !== -1}
                                    tabIndex={-1}
                                    disableRipple
                                />
                            </ListItemIcon>
                            <ListItemText primary={
                                <div className={GlobalClassKeys.textLineBreak}>
                                    {v.text}
                                </div>
                            } />
                        </ListItem>
                    );
                })
            }
        </div>
    );
};

const QuizSimpleChoice: React.FC<IQuizChoiceProps & StyledComponentProps<ClassKeyOfStyles<typeof styles>>> = (props) => {
    const { classes, item, selectedTests, onSelect } = props;

    return (
        <div>
            {
                item.alternatives.map((v, index) => {

                    var cls = classNames(
                        {
                            [classes.simpleChoiseContainer]: true,
                            [classes.simpleChoiseItem]: true,
                            [classes.simpleChoiceSelected]: selectedTests.findIndex(p => p == v.alternativeId) > -1
                        }
                    );

                    return (
                        <div key={index} onClick={() => onSelect(v)}>
                            <div className={cls} >
                                {v.text}
                            </div>
                        </div>
                    );
                })
            }
        </div>
    );
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IQuizChoiceResultProps {
    item: QuizItemDto;
    answerResult: QuizAnswerResultDto;
}

const QuizSimpleChoiceResult: React.FC<IQuizChoiceResultProps & StyledComponentProps<ClassKeyOfStyles<typeof styles>>> = (props) => {
    const { classes, item, answerResult } = props;

    return (
        <table className={classes.resultTable}>
            {
                item.alternatives.map((v, index) => {

                    const answer = answerResult.alternatives.find(p => p.alternativeId == v.alternativeId);

                    const clsAnswer = classNames(
                        {
                            [classes.simpleChoiseContainer]: true,
                            [classes.simpleChoiseResultItem]: true,
                            [classes.itemSelected]: answer.selected,
                            [classes.simpleChoiceCorrect]: answer.selected && answer.shouldBeSelected === true,
                            [classes.simpleChoiceIncorrect]: answer.selected && answer.shouldBeSelected === false
                        }
                    );

                    const clsFeedback = classNames(
                        {
                            [classes.itemFeedback]: true,
                            [classes.itemFeedbackCorrect]: answer.selected && answer.correct,
                            [classes.itemFeedbackIncorrect]: answer.selected && !answer.correct
                        }
                    );

                    return (
                        <tr key={index} >
                            <td>
                                <div className={clsAnswer} >
                                    {v.text}
                                </div>
                            </td>
                            {
                                answer.showCorrectAnswer &&
                                <td className={clsFeedback} >
                                    {
                                        answer.shouldBeSelected === true
                                            ? <FontAwesomeIcon icon={faCheckCircle} size="2x" />
                                            : <FontAwesomeIcon icon={faTimesCircle} size="2x" />
                                    }
                                </td>
                            }
                        </tr>
                    );
                })
            }
        </table>
    );
};

const QuizMultiChoiceResult: React.FC<IQuizChoiceResultProps & StyledComponentProps<ClassKeyOfStyles<typeof styles>>> = (props) => {
    const { classes, item, answerResult } = props;

    return (
        <table className={classes.resultTable}>
            {
                item.alternatives.map((v, index) => {

                    const answer = answerResult.alternatives.find(p => p.alternativeId == v.alternativeId);

                    const clsAnswer = classNames(
                        {
                            [classes.itemSelected]: answer.selected,
                            [classes.multiChoiceCorrect]: answer.showCorrectAnswer && answer.correct,
                            [classes.multiChoiceIncorrect]: answer.showCorrectAnswer && !answer.correct
                        }
                    );

                    const clsFeedback = classNames(
                        {
                            [classes.itemFeedback]: true,
                            [classes.itemFeedbackCorrect]: answer.correct,
                            [classes.itemFeedbackIncorrect]: !answer.correct
                        }
                    );

                    return (
                        <tr key={index} >
                            <td className={clsAnswer} style={{ width: 1 }} >
                                {
                                    answer.selected
                                        ? <FontAwesomeIcon icon={faCheckSquare} size="2x" />
                                        : <FontAwesomeIcon icon={faSquare} size="2x" />
                                }
                            </td>
                            <td>
                                <div className={clsAnswer} >
                                    {v.text}
                                </div>
                            </td>
                            {
                                answer.showCorrectAnswer &&
                                <td className={clsFeedback}>
                                    {
                                        answer.correct === true
                                            ? <FontAwesomeIcon icon={faCheckCircle} size="2x" />
                                            : <FontAwesomeIcon icon={faTimesCircle} size="2x" />
                                    }
                                </td>
                            }
                        </tr>
                    );
                })
            }
        </table>
    );
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    session: QuizSessionDto;
    currentItem: QuizItemDto;
    nextItem: QuizItemDto;
    answerResult: QuizAnswerResultDto;
    sessionResult: QuizSessionResultModel;
    finished: boolean;

    loading: boolean;
    saving: boolean;
    creatingNewTopic: boolean;
    lastError: any;
    creatingNewTopicFailed: React.ReactNode;
    awardConquested: AwardResult;
    selectedItems: string[];

    dialog: any;
}

interface IProps {
}

interface IRouteProps {
    id: string;
}

type QuizExecuteProps = IProps & RouteComponentProps<IRouteProps>;

type Props = IProps & WithStyles<typeof styles> & WithSnackbar & WithTranslation & QuizExecuteProps;

export class QuizExecute extends React.Component<Props, IState> {

    private sessionId: string;

    constructor(props: Props) {
        super(props);

        this.state = {
            session: null,
            answerResult: null,
            sessionResult: null,
            currentItem: null,
            nextItem: null,
            finished: false,
            awardConquested: null,

            lastError: null,

            loading: true,
            saving: false,
            creatingNewTopic: false,
            creatingNewTopicFailed: null,

            selectedItems: [],

            dialog: null,
        };

        this.sessionId = this.props.match.params.id;
    }

    ///
    private loadSession() {
        api.quizGetSession(this.sessionId)
            .then((session) => {

                this.setState({
                    session: session,
                    currentItem: session.firstItem,
                    sessionResult: session.result,
                    finished: session.result != null,
                    loading: false,
                });

            })
            .catch((error) => {
                this.setState({ lastError: error, loading: false });
            });
    }

    ///
    private save(trustLevel: KnowledgeTestTrustLevel) {

        const { selectedItems, currentItem } = this.state;

        this.setState({ saving: true });

        api.quizSaveAnswer(
            {
                sessionId: this.sessionId,
                testId: currentItem.testId,
                alternatives: selectedItems,
                trustLevel
            })
            .then((result) => {

                this.setState({
                    nextItem: result.nextItem,
                    answerResult: result.answerResult,
                    sessionResult: result.performance,
                    session: { ...this.state.session, ...{ maxItems: result.maxItems } },
                    saving: false,
                    awardConquested: result.award
                });
            })
            .catch((error) => {
                this.setState({ lastError: error, saving: false });
            });
    }

    ///
    private createNewTopicSession() {

        this.setState({ creatingNewTopic: true });

        api.quizCreateTopicSession(Session.user.id, this.state.session.topicId)
            .then((sessionId) => {
                if (sessionId == null) {
                    this.setState({
                        creatingNewTopic: false,
                        creatingNewTopicFailed: <MuiMessageBox message={this.props.t("Views.Quiz.QuizExecute.CreateSessionError")} variant="error" />
                    });
                }
                else {
                    this.props.history.push(RoutePaths.QuizExecute.replace(":id", sessionId));
                }
            })
            .catch((error) => {
                this.setState({
                    creatingNewTopic: false,
                    creatingNewTopicFailed: <RequestErrorReport error={error} />
                });
            });
    }

    ///
    private handleSelection(selection: QuizAlternativeDto) {

        const { currentItem } = this.state;

        let selectedItems;

        if (currentItem.answerSelectionMode == KnowledgeTestSelectionMode.Simple) {
            selectedItems = [selection.alternativeId];
        }
        else {
            selectedItems = [...this.state.selectedItems];

            const currentIndex = selectedItems.indexOf(selection.alternativeId);

            if (currentIndex === -1) {
                selectedItems.push(selection.alternativeId);
            } else {
                selectedItems.splice(currentIndex, 1);
            }
        }

        this.setState({ selectedItems });
    }

    ///
    private handleNextItem = () => {
        this.setState({
            answerResult: null,
            selectedItems: [],
            currentItem: this.state.nextItem
        });
    };

    ///
    private handleFinished = () => {
        this.setState({
            finished: true,
        }, () => {
            if (this.state.awardConquested)
                this.props.snackbar.showMessage(<AwardSnackbarMessage award={this.state.awardConquested} />, "trophy");
        });
    };

    ///
    private handleNewSession = () => {
        const { sessionResult } = this.state;
        if (sessionResult.completePercent == 100) {
            this.props.history.replace(RoutePaths.QuizSelection);
        }
        else {

            this.setState({
                dialog: (
                    <MuiConfirmation
                        message={this.props.t("Views.Quiz.QuizExecute.ConfirmationNewSessionMessage")}
                        onClose={
                            (result) => {
                                if (result) {
                                    this.setState({ dialog: null }, () => this.createNewTopicSession());
                                }
                                else {
                                    this.props.history.replace(RoutePaths.QuizSelection);
                                }
                            }
                        }
                    />
                )
            });
        }
    };

    ///
    private handleGotoFeed = () => {
        this.props.history.replace(RoutePaths.TimelineStartup);
    };

    ///
    private handleMyEvolution = () => {
        this.props.history.replace(RoutePaths.UserEvolutionDashboard$Knowledge);
    };

    ///
    private renderTrustLevel() {
        return (
            <MuiBox variant="warning" >
                <Grid container direction="column" justifyContent="center" alignItems="center">
                    <Grid item xs>
                        <b>{this.props.t("Views.Quiz.QuizExecute.TrustLevelMessage")}</b>
                    </Grid>
                    <Grid item xs>

                        <Grid container justifyContent="space-around">
                            <MuiRegularButton color="info" onClick={() => this.save(KnowledgeTestTrustLevel.Low)}><KnowledgeTestTrustLevelName level={KnowledgeTestTrustLevel.Low} /></MuiRegularButton>
                            <MuiRegularButton color="info" onClick={() => this.save(KnowledgeTestTrustLevel.Medium)}><KnowledgeTestTrustLevelName level={KnowledgeTestTrustLevel.Medium} /></MuiRegularButton>
                            <MuiRegularButton color="info" onClick={() => this.save(KnowledgeTestTrustLevel.High)}><KnowledgeTestTrustLevelName level={KnowledgeTestTrustLevel.High} /></MuiRegularButton>
                        </Grid>

                    </Grid>
                </Grid>
            </MuiBox >
        );
    }

    ///
    private renderItemResult() {
        const { answerResult, currentItem } = this.state;
        return currentItem.answerSelectionMode == KnowledgeTestSelectionMode.Simple
            ? <QuizSimpleChoiceResult item={currentItem} answerResult={answerResult} classes={this.props.classes} />
            : <QuizMultiChoiceResult item={currentItem} answerResult={answerResult} classes={this.props.classes} />;
    }

    ///
    private renderItemResultFooter() {
        const { nextItem, answerResult, currentItem } = this.state;
        const { classes } = this.props;

        const clsFeedback = classNames(
            {
                [classes.tooltip]: true,
                [answerResult.correct ? "correct" : "incorrect"]: true
            }
        );

        var feedbackTexts: string[] = [];
        if (currentItem.answerSelectionMode == KnowledgeTestSelectionMode.Multi) {
            answerResult.alternatives.filter(p => p.feedback && p.correct == answerResult.correct).map((v) => {
                if (feedbackTexts.find(x => x == v.feedback) == null) feedbackTexts.push(v.feedback);
            });
        }
        else {
            answerResult.alternatives.filter(p => p.feedback && p.selected).map((v) => {
                if (feedbackTexts.find(x => x == v.feedback) == null) feedbackTexts.push(v.feedback);
            });
        }

        return (
            <div>
                <MuiContainer>
                    <Grid container justifyContent="flex-end">
                        <Grid item>
                            {
                                nextItem
                                    ? (
                                        <MuiRegularButton
                                            color="warning"
                                            onClick={this.handleNextItem}
                                        >
                                            <span>{this.props.t("Views.Quiz.QuizExecute.ButtonNextTest")}</span>
                                        </MuiRegularButton>
                                    )
                                    : (
                                        <MuiRegularButton
                                            color="success"
                                            onClick={this.handleFinished}
                                        >
                                            <span>{this.props.t("Views.Quiz.QuizExecute.ButtonFinishText")}</span>
                                        </MuiRegularButton>
                                    )
                            }
                        </Grid>
                    </Grid>
                </MuiContainer>
                {
                    feedbackTexts.length > 0 &&
                    <Grid container wrap="nowrap">
                        <Grid item>
                            <img src={AppImages.TheGenieRight} height={250} alt="Genie Logo" />
                        </Grid>
                        <Grid item>
                            <div className={clsFeedback}>
                                <ul>
                                    {
                                        feedbackTexts.map((v) => <li><ReactSplitString splitChar={'\n'} separator={<br />} text={v} /></li>)
                                    }
                                </ul>
                            </div>
                        </Grid>
                    </Grid>
                }
            </div>
        );
    }

    ///
    private renderItemAlternative() {

        const { selectedItems, currentItem } = this.state;

        return (
            currentItem.answerSelectionMode == KnowledgeTestSelectionMode.Simple
                ? <QuizSimpleChoice item={currentItem} selectedTests={selectedItems} onSelect={(alternative) => this.handleSelection(alternative)} classes={this.props.classes} />
                : <QuizMultiChoice item={currentItem} selectedTests={selectedItems} onSelect={(alternative) => this.handleSelection(alternative)} />
        );
    }

    ///
    private renderItemAlternativeFooter() {

        const { selectedItems, currentItem } = this.state;

        return (
            (selectedItems.length > 0 || currentItem.answerSelectionMode == KnowledgeTestSelectionMode.Multi) &&
            <MuiContainer>
                {this.renderTrustLevel()}
            </MuiContainer>
        );
    }

    ///
    private renderItem() {

        const { currentItem: { attach, title }, answerResult } = this.state;

        return (
            <div>
                <MuiContainer>

                    <MuiContainer>
                        <Typography variant="h5" className={GlobalClassKeys.textLineBreak}>
                            {title}
                        </Typography>
                    </MuiContainer>

                    <Grid container spacing={2} justifyContent="space-between" alignItems="flex-start">
                        {
                            attach && attach.mainFile &&
                            <Grid item style={{ maxWidth: 250 }}>
                                {
                                    attach.mainFile.mimeType.indexOf("image") > -1
                                        ? <ImageZoom image={attach.mainFile} thumbnail={attach.thumbnail} width="100%" />
                                        : <QuizVideo fileOrEmbedVideo={attach.mainFile} />
                                }
                            </Grid>
                        }

                        <Grid item xs>
                            {
                                answerResult
                                    ? this.renderItemResult()
                                    : this.renderItemAlternative()
                            }
                        </Grid>
                    </Grid>
                </MuiContainer>

                {
                    answerResult
                        ? this.renderItemResultFooter()
                        : this.renderItemAlternativeFooter()
                }

            </div>
        );
    }

    ///
    private renderHeader() {
        const { session, currentItem } = this.state;
        if (session == null || currentItem == null) return null;

        const percent = currentItem.currentIndex / session.maxItems * 100.0;

        return (
            <div>
                <Grid container justifyContent="space-between" alignItems="center" spacing={3} >
                    <Grid item xs>
                        <MuiMuted>
                            {session.topicName}
                        </MuiMuted>
                    </Grid>
                    <Grid item>
                        <ProgressBar
                            color="secondary"
                            maxWidth={250}
                            percent={percent}
                            text={<small>{this.props.t("Views.Quiz.QuizExecute.QuestionNavigator", { current: currentItem.currentIndex, total: session.maxItems })}</small>}
                        />
                    </Grid>
                </Grid >

            </div>
        );
    }

    ///
    private renderFinishedResult() {
        const { session, sessionResult: { awardPoints, currentLevel, knowledgePercent, totalAnswers, totalCorrect }, awardConquested } = this.state;

        return (
            <Grid container justifyContent="space-between">
                <Grid item>
                    <img src={AppImages.TheGenieRight} height={250} alt="Genie Logo" />
                </Grid>
                <Grid item xs>
                    <MuiContainer>
                        <MuiPaper borderColor="mutedDark" >

                            <Typography variant="h6" style={{ textAlign: "center" }}>
                                <span>{this.props.t("Views.Quiz.QuizExecute.FinishedPanelTitle")}</span>
                            </Typography>

                            <MuiContainer>
                                <Grid container justifyContent="space-between">
                                    <Grid item>

                                        <Grid container justifyContent="center" alignItems="center" direction="column">
                                            <Grid item>
                                                <div className={GlobalClassKeys.textCenter}>
                                                    <IconCorrectAnswers size="4x" color="success" />
                                                    <br />
                                                    <Typography variant="h6"><b>{Math.round(totalCorrect / totalAnswers * 100.0)}%</b></Typography>
                                                    <Typography variant="button">{this.props.t("Views.Quiz.QuizExecute.CorrectAnswersText")}</Typography>
                                                </div>
                                            </Grid>
                                        </Grid>

                                    </Grid>
                                    <Grid item>

                                        <Grid container justifyContent="center" alignItems="center" direction="column">
                                            <Grid item>
                                                <div className={GlobalClassKeys.textCenter}>
                                                    <IconTrophyAlt size="4x" color="info" />
                                                    <br />
                                                    <Typography variant="h6"><b>+{awardPoints}</b></Typography>
                                                    <Typography variant="button">{this.props.t("Views.Quiz.QuizExecute.PointsText")}</Typography>
                                                </div>
                                            </Grid>
                                        </Grid>

                                    </Grid>
                                </Grid>
                            </MuiContainer>

                            <KnowledgeTopicLevel
                                knowledgePercent={knowledgePercent}
                                topicName={session.topicName}
                                topicThumbnailUrl={session.topicThumbnailUrl}
                            />

                        </MuiPaper>
                    </MuiContainer>

                    <MuiContainer>
                        <div className={GlobalClassKeys.textCenter}>
                            <Typography variant="h6">{this.props.t("Views.Quiz.QuizExecute.NextActionText")}</Typography>
                        </div>
                        <Grid container justifyContent="center">
                            <Grid item>
                                <MuiRegularButton color="info" onClick={this.handleNewSession}>
                                    <IconQuiz fixedWidth />
                                    <span>&nbsp;{this.props.t("Views.Quiz.QuizExecute.ActionNewSessionText")}</span>
                                </MuiRegularButton>

                                <MuiRegularButton color="info" onClick={this.handleMyEvolution}>
                                    <IconEvolution fixedWidth />
                                    <span>&nbsp;{this.props.t("Views.Quiz.QuizExecute.ActionMyEvoluation")}</span>
                                </MuiRegularButton>

                                <MuiRegularButton color="info" onClick={this.handleGotoFeed}>
                                    <IconTimeline fixedWidth />
                                    <span>&nbsp;{this.props.t("Views.Quiz.QuizExecute.ActionGotoKnowledge")}</span>
                                </MuiRegularButton>
                            </Grid>
                        </Grid>
                    </MuiContainer>
                </Grid>
            </Grid>
        );
    }

    ///
    private renderFinished() {
        const { creatingNewTopic, creatingNewTopicFailed } = this.state;

        if (creatingNewTopicFailed) {
            return (
                <div className={this.props.classes.container}>
                    {creatingNewTopicFailed}
                </div>
            );
        }

        return (
            <>
                {this.state.dialog}

                {creatingNewTopic && <RequestIndicator type="request" />}

                {this.renderFinishedResult()}

            </>
        );
    }

    ///
    private checkForUpdateState(prevProps: QuizExecuteProps) {
        if (prevProps.match.params.id != this.props.match.params.id) {

            // RESET EXECUTION

            this.sessionId = this.props.match.params.id;

            this.setState({
                session: null,
                answerResult: null,
                sessionResult: null,
                currentItem: null,
                nextItem: null,
                finished: false,

                lastError: null,

                loading: true,
                saving: false,
                creatingNewTopic: false,
                creatingNewTopicFailed: null,

                selectedItems: [],

                dialog: null,
            }, () => this.loadSession());
        }
    }

    ///
    public componentDidMount() {
        this.loadSession();
    }

    ///
    public componentDidUpdate(prevProps: QuizExecuteProps) {
        this.checkForUpdateState(prevProps);
    }

    ///
    public render() {
        const { lastError, loading, saving, finished } = this.state;

        if (finished) {
            return this.renderFinished();
        }

        if (loading) {
            return <RequestIndicator type="request" />;
        }

        return (
            <div className={this.props.classes.container}>
                {
                    lastError
                        ? <RequestErrorReport error={lastError} />
                        : (
                            <>
                                {saving && <RequestIndicator type="post" />}

                                <MuiContainer>
                                    {this.renderHeader()}
                                </MuiContainer>

                                <MuiContainer>
                                    {this.renderItem()}
                                </MuiContainer>
                            </>
                        )
                }
            </div>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

export default
    withRouter(
        withSnackbar(
            withTranslation()(
                withStyles(styles)(
                    QuizExecute
                )
            )
        )
    );